import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Calendar, ArrowLeft } from 'lucide-react';
import { useSocialStore } from '../store/socialStore';
import { useAuthStore } from '../store';
import { apiService } from '../services/api';
import { SharedProjectCard } from '../types';
import FollowButton from '../components/social/FollowButton';
import ProjectCard from '../components/social/ProjectCard';
import FollowersFollowingModal from '../components/social/FollowersFollowingModal';
import { useContextualNavigation } from '../hooks/useContextualNavigation';

const UserProfilePage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { navigateWithContext, getBackNavigation } = useContextualNavigation();
  const backNav = getBackNavigation('/explore', 'Back to Explore');
  const { user } = useAuthStore();
  const { viewedProfile, profileLoading, loadUserProfile, followUser, unfollowUser } =
    useSocialStore();
  const currentUserId = (user as any)?.user_id ?? user?.id;
  const isOwnProfile = currentUserId && viewedProfile
    ? String(currentUserId) === String(viewedProfile.user_id)
    : false;

  const [projects, setProjects] = useState<SharedProjectCard[]>([]);
  const [modalType, setModalType] = useState<'followers' | 'following' | null>(null);

  const numericUserId = userId ? parseInt(userId, 10) : 0;

  useEffect(() => {
    if (numericUserId) {
      loadUserProfile(numericUserId);
    }
  }, [numericUserId, loadUserProfile]);

  useEffect(() => {
    if (numericUserId) {
      apiService.getExploreProjects('recent', 0, 20, numericUserId)
        .then((response) => {
          setProjects(response.projects);
        })
        .catch((error) => {
          console.error('Failed to load user projects:', error);
        });
    }
  }, [numericUserId]);

  const handleFollowToggle = async () => {
    if (!viewedProfile) return;
    if (viewedProfile.is_following) {
      await unfollowUser(viewedProfile.user_id);
    } else {
      await followUser(viewedProfile.user_id);
    }
  };

  const handleProjectClick = (projectId: number) => {
    navigateWithContext(`/shared/projects/${projectId}`, viewedProfile?.username ? `@${viewedProfile.username}` : 'Profile');
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return '';
    }
  };

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading profile...</p>
      </div>
    );
  }

  if (!viewedProfile) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">User not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Back Navigation */}
      <Link
        to={backNav.path}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        {backNav.label}
      </Link>

      {/* Profile Header */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <User className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                @{viewedProfile.username}
              </h1>
              {viewedProfile.created_at && (
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Joined {formatDate(viewedProfile.created_at)}
                </p>
              )}
            </div>
          </div>

          {!isOwnProfile && (
            <FollowButton
              userId={viewedProfile.user_id}
              isFollowing={viewedProfile.is_following}
              onToggle={handleFollowToggle}
            />
          )}
        </div>

        {/* Stats */}
        <div className="flex gap-6 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {viewedProfile.public_project_count}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Operations</p>
          </div>
          <button
            onClick={() => setModalType('followers')}
            className="text-center hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {viewedProfile.follower_count}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Followers</p>
          </button>
          <button
            onClick={() => setModalType('following')}
            className="text-center hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {viewedProfile.following_count}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Following</p>
          </button>
        </div>
      </div>

      {/* Public Projects */}
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Public Projects
      </h2>
      {projects.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          No public projects yet.
        </p>
      )}
      {projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {projects.map((project) => (
            <ProjectCard
              key={project.project_id}
              project={project}
              onClick={handleProjectClick}
            />
          ))}
        </div>
      )}

      {/* Followers/Following Modal */}
      {modalType && (
        <FollowersFollowingModal
          userId={numericUserId}
          type={modalType}
          isOpen={true}
          onClose={() => setModalType(null)}
        />
      )}
    </div>
  );
};

export default UserProfilePage;
